/**
 * The follow-up that goes into LinkedIn's own "Follow-up-Nachricht" when the InMail is sent with
 * "Add LinkedIn's own follow-up" ticked (Sofia, Oct 15 2026: show it in the queue, so it can be
 * read and changed BEFORE it is scheduled). LinkedIn sends it after 7 days if there is no reply.
 */
import { useState } from 'react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { showToast } from '@finn-auto/design-system/atoms/toast';
import * as api from '../../services/taOutreachApi';
import type { OutreachCandidate } from '../../types/taOutreach';
import { MessageCard } from './MessageCard';
import { RewriteBar } from './RewriteBar';

export function FollowUpEditor({
  candidate: c,
  subject,
  onUpdated,
}: {
  candidate: OutreachCandidate;
  /** The InMail's subject as it stands in the editor; the follow-up goes out as "Re: …". */
  subject: string;
  onUpdated: (item: OutreachCandidate) => void;
}) {
  const saved = c.followUpPreview || '';
  const [text, setText] = useState(saved);
  const [busy, setBusy] = useState(false);
  // A new value from the server (save, reset, reload) replaces the editor text.
  const [syncedFrom, setSyncedFrom] = useState(saved);
  if (syncedFrom !== saved) {
    setSyncedFrom(saved);
    setText(saved);
  }
  const dirty = text.trim() !== saved.trim();

  async function save(next: string) {
    setBusy(true);
    try {
      const r = await api.saveFollowUpText(c.id, next);
      onUpdated(r.item);
    } catch (err) {
      showToast({ status: 'error', position: 'bottomCenter', description: err instanceof Error ? err.message : 'Could not save the follow-up.' });
    } finally {
      setBusy(false);
    }
  }

  if (!saved) return null;
  return (
    <MessageCard
      title="Follow-up"
      hint={
        <>
          Sent by LinkedIn after 7 days only if there is no reply · {c.followUpEdited ? 'edited by you' : 'standard text'}
        </>
      }
      subject={subject ? `Re: ${subject}`.slice(0, 80) : ''}
      body={text}
      onBody={setText}
      disabled={busy}
      minRows={3}
      assist={
        <RewriteBar
          kind="follow_up"
          candidateId={c.id}
          subject={subject ? `Re: ${subject}`.slice(0, 80) : 'Follow-up'}
          body={text}
          disabled={busy}
          placeholder="Help me write the follow-up: shorter, in German, more personal…"
          onApply={(r) => setText(r.body)}
        />
      }
      footer={
        (dirty || c.followUpEdited) && (
          <div className="flex flex-wrap gap-2">
            {dirty && (
              <Button variant="secondary" size="sm" disabled={busy || !text.trim()} onClick={() => void save(text)}>
                Save follow-up
              </Button>
            )}
            {c.followUpEdited && !dirty && (
              <Button variant="ghost" size="sm" disabled={busy} onClick={() => void save('')}>
                Back to the standard text
              </Button>
            )}
          </div>
        )
      }
    />
  );
}
